import { useEffect, useState, type MouseEvent, type ReactNode } from 'react';
import { Box, Image, Link, Spinner, Stack, Text } from '@chakra-ui/react';
import type { Bridge } from '@casa/bridge';
import { listServerDashboards } from '../data/casa_server.ts';
import { dashboardSlug } from '../data/tab_dashboard.ts';
import { goToSettings } from '../settings/settings_navigation.ts';
import { ChromeError } from '../ui/chrome_page.tsx';
import { followToPage } from '../ui/route.ts';
import { SettingsGroup, SettingsRow } from '../settings/settings_ui.tsx';

// The root page of a Tilez Hub in a browser (docs/specs/settings.md §4.7, the owner 2026-11-04):
// its dashboards, each a link to /dash/<name>, which shows it in that tab only, so that two tabs or
// two browsers can show two dashboards; this browser's own dashboard at /dash; the settings and the
// admin page. Plain links: a new tab is the browser's own (middle click, long press).

export function HomePage({ shell }: { shell: Bridge }) : ReactNode {
    const [names, setNames] = useState<string[]>();
    const [error, setError] = useState<string>();

    useEffect(() => {
        listServerDashboards(shell, { kind: 'page' }).then(
            (entries) => setNames(entries.map((entry) => entry.name)),
            (caught: unknown) => setError(caught instanceof Error ? caught.message : String(caught)),
        );
    }, [shell]);

    const link = (href: string, row: ReactNode, onClick?: (event: MouseEvent<HTMLAnchorElement>) => void) : ReactNode => (
        <Link href={href} onClick={onClick} display="block" textDecoration="none" _hover={{ textDecoration: 'none' }}>
            {row}
        </Link>
    );

    return (
        <Box className="chrome" height="100%" overflowY="auto" bg="bg" color="fg">
            <Stack gap="8" px={{ base: '4', md: '8' }} py={{ base: '10', md: '16' }} maxW="md" mx="auto">
                <Stack gap="3" align="center" textAlign="center">
                    <Image src="/app-icon.svg" alt="" boxSize="16" borderRadius="xl" />
                    <Text fontSize="3xl" fontWeight="bold">
                        Tilez
                    </Text>
                </Stack>

                <SettingsGroup title="Dashboards on this server" note="Each opens in this tab only: open two in two tabs to see them side by side.">
                    {names === undefined && !error ? <Spinner m="4" /> : null}
                    {names?.length === 0 ? <SettingsRow title={<Text as="span" color="fg.muted">None yet</Text>} detail="Make one from this browser's dashboard" /> : null}
                    {names?.map((name) => (
                        <Box key={name} data-testid={`dashboard-${name}`}>
                            {link(`/dash/${dashboardSlug(name)}`, <SettingsRow icon="table-cells-large" title={name} detail={`/dash/${dashboardSlug(name)}`} />)}
                        </Box>
                    ))}
                </SettingsGroup>
                {error ? <ChromeError text={error} /> : null}

                <SettingsGroup>
                    {link('/dash', <SettingsRow icon="house" title="This browser's dashboard" detail="The one it shows, or a new one" />)}
                    {link('/settings', <SettingsRow icon="gear" title="Settings" />, (event) => {
                        // Over this page, back here on Done; a click for a new tab is the browser's.
                        if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
                            event.preventDefault();
                            goToSettings('');
                        }
                    })}
                    {link('/admin', <SettingsRow icon="server" title="Admin page" detail="Dashboards and paired devices" />, (event) => followToPage(event, '/admin'))}
                </SettingsGroup>
            </Stack>
        </Box>
    );
}
